import React from "react";
import { useTranslation } from "react-i18next";

function StatCard({ label, value, unit, color = "indigo" }) {
  const colors = {
    indigo: "text-indigo-600",
    green: "text-green-600",
    red: "text-red-500",
    gray: "text-gray-500",
  };
  return (
    <div className="bg-gray-50 rounded-lg p-3 text-center shadow-sm">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-lg font-bold ${colors[color]}`}>
        {value != null ? Number(value).toFixed(2) : "—"} <span className="text-xs font-normal text-gray-400">{unit}</span>
      </p>
    </div>
  );
}

function RelayBadge({ status }) {
  const { t } = useTranslation();
  if (status === null || status === undefined) return null;
  const val = Number(status);
  const isActive  = (val & 1) !== 0;
  const isLatched = (val & 2) !== 0;

  const label = isActive && isLatched
    ? `${t("voltampermetre.relayActive")} / ${t("voltampermetre.relayLatched")}`
    : isActive
      ? t("voltampermetre.relayActive")
      : isLatched
        ? t("voltampermetre.relayLatched")
        : t("voltampermetre.relayInactive");

  const cls = isActive
    ? "bg-green-100 text-green-700 border-green-300"
    : "bg-gray-100 text-gray-500 border-gray-300";

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${cls}`}>
      {t("voltampermetre.relayStatus")}: {label}
    </span>
  );
}

export default function VoltampermeDashboard({ data }) {
  const { t } = useTranslation();

  if (!data) {
    return (
      <div className="p-6 text-center text-slate-500">{t("hierarchy.loading")}</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Voltage block */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">
          {t("voltampermetre.voltage")}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label={t("voltampermetre.liveVoltage")} value={data.V}    unit="V" color="indigo" />
          <StatCard label={t("common.min")}                 value={data.Vmin} unit="V" color="gray" />
          <StatCard label={t("common.max")}                 value={data.Vmax} unit="V" color="red" />
        </div>
      </div>

      {/* Current block */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">
          {t("voltampermetre.current")}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label={t("voltampermetre.liveCurrent")} value={data.I}    unit="A" color="green" />
          <StatCard label={t("common.min")}                 value={data.Imin} unit="A" color="gray" />
          <StatCard label={t("common.max")}                 value={data.Imax} unit="A" color="red" />
        </div>
      </div>

      {/* Relay status */}
      {data.relayStatus !== null && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 flex items-center gap-3">
          <RelayBadge status={data.relayStatus} />
        </div>
      )}
    </div>
  );
}
